import React, { Component } from 'react';
import moment from 'moment';
import { connect, ConnectedProps } from 'react-redux';
import { Button } from '@mui/material';
import { NavigateFunction } from 'react-router-dom';

import { FormattedMessage, injectIntl } from 'react-intl';
import ContentBox from '../../components/layout/ContentBox';
import TrialManagement from '../../components/trial/TrialManagement';
import TrialSearch from '../../components/trial/TrialSearch';
import { redirectInternalNewTab } from '../../utilities/urlHelper';
import ApiClient from '../../loopback/apiClient';
import { ZerpaTableStateSetters } from '../../components/layout/table/ZerpaTable';

import {
  updateTrial,
  showNotification,
  showErrorNotification,
  createCertificates,
  createBookingsList,
  importDeliveryList,
  downloadInvoicesZip,
  fetchTrialTags,
  setPageSearchText,
  setYear,
  setType,
  setSelectedTab,
  setShowDeactivated,
  setZerpaTableState,
  setLoading,
  importDeliveryListV2,
} from '../../redux/actions';
import {
  RootState,
  Trial,
  ThirdPartyTrial,
  TrialTagMapping,
  ZerpaState,
} from '../../redux/types';
import TrialTypeTabs from '../../components/trial/TrialTypeTabs';
import AddTrialDialog from '../AddTrialDialog';

const pageIdentifier = 'trialmanagement';

type TrialExtended = Trial & {
  thirdPartyTrial?: ThirdPartyTrial,
  trialTagMapping?: TrialTagMapping[],
};
type Props = PropsFromRedux & {
  navigate: NavigateFunction;
};
type State = {
  trials?: TrialExtended[];
  originalTrials?: TrialExtended[];
  openNewTrialDialog: boolean;
  duplicateTrial?: Trial;
}

class TrialManagementTab extends Component<Props, State> {
  static onGotoSurvey = (trial: Trial) => {
    if (trial.version === 2) {
      redirectInternalNewTab(`trial/${trial.id}/survey`);
    } else {
      redirectInternalNewTab(`survey/${trial.id}`);
    }
  };

  static onGotoTrialDetail = (trialId: number) => {
    redirectInternalNewTab(`trialdetail/${trialId}`);
  };

  static onEditGroundTruth = (trial: Trial) => {
    redirectInternalNewTab(`groundTruth/${trial.id}`);
  };

  zerpaTableSetter?: ZerpaTableStateSetters;

  constructor(props: Props) {
    super(props);
    this.state = {
      openNewTrialDialog: false,
    };
  }

  componentDidMount() {
    const { fetchTrialTags: fetchTrialTagsAction, trialTags } = this.props;
    this.fetchTrialsToState();
    if (!trialTags) {
      fetchTrialTagsAction();
    }
  }

  componentDidUpdate(prevProps: Props) {
    const {
      query,
      tags,
      year,
    } = this.props;

    const { query: pQuery, tags: pTags, year: pYear } = prevProps;
    const setPageTo0 = query !== pQuery || year !== pYear || tags?.length !== pTags?.length;
    if (query !== pQuery || year !== pYear) {
      this.fetchTrialsToState();
    }
    if (tags?.length !== pTags?.length) {
      this.filterTrialsWithTags();
    }
    if (this.zerpaTableSetter?.setPage && setPageTo0) {
      this.zerpaTableSetter?.setPage(0);
    }
  }

  fetchTrialsToState = async () => {
    const {
      query, year, tags, setLoading: dispatchSetLoading,
    } = this.props;
    const filter: any = {
      where: {
        startDate: {
          between: [
            moment().year(Number(year)).startOf('year'),
            moment().year(Number(year)).endOf('year'),
          ],
        },
      },
      include: 'trialTagMapping',
    };

    if (query) {
      filter.where.or = [];
      filter.where.or.push({ name: { like: `%${query}%` } });
      filter.where.or.push({ code: { like: `%${query}%` } });
    }
    dispatchSetLoading(true);
    const trials = await ApiClient.getTrials(filter);
    this.setState({ trials, originalTrials: trials });
    if (tags.length) {
      this.filterTrialsWithTags();
    }
    dispatchSetLoading(false);
  };

  filterTrialsWithTags = () => {
    const { originalTrials } = this.state;
    const { tags } = this.props;
    if (tags?.length === 0) {
      this.fetchTrialsToState();
      return;
    }
    const filteredTrialsWithTags = originalTrials?.filter((trial) => {
      const { trialTagMapping } = trial;
      if (!trialTagMapping || trialTagMapping.length === 0) {
        return false;
      }
      const tagIds = trialTagMapping.map((tag) => tag.tagId);
      return tags?.find((tag) => tagIds.includes(tag));
    });
    this.setState({ trials: filteredTrialsWithTags });
  };

  onZerpaTableStateChange = (
    zerpaState: ZerpaState,
    stateSetters: ZerpaTableStateSetters,
  ) => {
    const { setZerpaTableState: setZerpaTableStateAction } = this.props;
    setZerpaTableStateAction(zerpaState);
    if (!this.zerpaTableSetter) {
      this.zerpaTableSetter = stateSetters;
    }
  };

  goToReviewManagement = (trial: Trial) => {
    const { navigate } = this.props;
    navigate('/reviewmanagement', {
      state: {
        trial,
      },
    });
  };

  onSearch = ({ searchQuery, tags, year }: {
    searchQuery: string,
    tags: number[],
    year: string
  }) => {
    const {
      setPageSearchText: setPageSearchTextAction,
      setYear: setYearAction,
      setType: setTypeAction,
    } = this.props;
    setPageSearchTextAction(searchQuery);
    setYearAction(year);
    setTypeAction(tags);
  };

  onShowDeactivatedTrialsClicked = (checked: boolean) => {
    const { setShowDeactivated: setShowDeactivatedAction } = this.props;
    setShowDeactivatedAction(checked);
  };

  onTypeTabChange = (tabId: 'institute' | 'individual') => {
    const { setSelectedTab: setSelectedTabAction } = this.props;
    setSelectedTabAction(tabId);
  };

  importDeliveries = (trialId: number, deliveryFileName: string) => {
    const {
      importDeliveryList: importDeliveryListAction,
      showErrorNotification: showErrorNotificationAction,
    } = this.props;
    const { trials: lbTrials } = this.state;
    importDeliveryListAction(trialId, deliveryFileName)
      .then((result: { deliveriesImported: number }) => {
        if (result.deliveriesImported > 0 && lbTrials) {
          this.fetchTrialsToState();
        } else {
          showErrorNotificationAction(<FormattedMessage id="trialmanagement.uploadDeliveryListFailed" />);
        }
      });
  };

  importDeliveriesV2 = (trialId: number, deliveryFileName: string) => {
    const {
      importDeliveryListV2: importDeliveryListAction,
      showErrorNotification: showErrorNotificationAction,
    } = this.props;
    const { trials: lbTrials } = this.state;
    importDeliveryListAction(trialId, deliveryFileName)
      .then((result: { deliveriesImported: number }) => {
        if (result.deliveriesImported > 0 && lbTrials) {
          this.fetchTrialsToState();
        } else {
          showErrorNotificationAction(<FormattedMessage id="trialmanagement.uploadDeliveryListFailed" />);
        }
      });
  };

  createTrial = (trial?: Trial) => {
    this.setState({
      openNewTrialDialog: true,
      duplicateTrial: trial,
    });
  };

  onNewTrialClick = () => {
    this.createTrial();
  };

  onCloseNewTrialClick = () => {
    this.setState({ openNewTrialDialog: false, duplicateTrial: undefined });
  };

  render() {
    const {
      locale,
      updateTrial: _updateTrial,
      createCertificates: _createCertificates,
      createBookingsList: _createBookingsList,
      downloadInvoicesZip: _downloadInvoicesZip,
      trialTags,
      selectedTab,
      showDeactivatedTrials,
      navigate,
      setLoading: dispatchSetLoading,
    } = this.props;
    const {
      trials: lbTrials,
      openNewTrialDialog,
      duplicateTrial,
    } = this.state;

    return (
      <>
        {
          lbTrials
          && (
            <ContentBox
              elevation={0}
              title={<FormattedMessage id="trialmanagement.headline" />}
            >
              <div style={{ marginTop: 20, marginBottom: 20 }}>
                <Button
                  variant="contained"
                  onClick={this.onNewTrialClick}
                  color="primary"
                >
                  <FormattedMessage id="trialmanagement.newTrialButton" />
                </Button>
              </div>
              <TrialTypeTabs
                onTabChange={this.onTypeTabChange}
                selectedTab={selectedTab}
              />
              <TrialSearch
                data={this.props}
                onSearch={this.onSearch}
                onShowDeactivatedTrials={this.onShowDeactivatedTrialsClicked}
                trialTags={trialTags}
              />
              <TrialManagement
                trials={lbTrials}
                updateTrial={_updateTrial}
                importDeliveryList={this.importDeliveries}
                importDeliveryListV2={this.importDeliveriesV2}
                createCertificates={_createCertificates}
                onCreateBookingsList={_createBookingsList}
                reloadTrials={this.fetchTrialsToState}
                locale={locale}
                editGroundTruth={TrialManagementTab.onEditGroundTruth}
                openTrialDetails={TrialManagementTab.onGotoTrialDetail}
                goToSurvey={TrialManagementTab.onGotoSurvey}
                createTrial={this.createTrial}
                goToReviewManagement={this.goToReviewManagement}
                downloadInvoicesZip={_downloadInvoicesZip}
                showDeactivatedTrials={showDeactivatedTrials}
                onZerpaTableStateChange={this.onZerpaTableStateChange}
                selectedTab={selectedTab}
                navigate={navigate}
                setLoading={dispatchSetLoading}
              />
            </ContentBox>
          )
        }
        <AddTrialDialog
          isOpen={openNewTrialDialog}
          onClose={this.onCloseNewTrialClick}
          trial={duplicateTrial}
        />
      </>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  locale: state.intl.locale,
  trialTags: state.trialTag.byId,
  query: state.pageState.trialmanagement.searchText,
  year: state.pageState.trialmanagement.year,
  tags: state.pageState.trialmanagement.type,
  selectedTab: state.pageState.trialmanagement.selectedTab,
  showDeactivatedTrials: state.pageState.trialmanagement.showDeactivatedTrials,
});

const mapDispatchToProps = (dispatch: any) => ({
  updateTrial: (trial: Trial) => dispatch(updateTrial(trial)),
  createCertificates: (trial: Trial) => dispatch(createCertificates(trial)),
  createBookingsList: (trial: Trial) => dispatch(createBookingsList(trial)),
  importDeliveryList: (
    trialId: number, filename: string,
  ) => dispatch(importDeliveryList(trialId, filename)),
  importDeliveryListV2: (
    trialId: number, filename: string,
  ) => dispatch(importDeliveryListV2(trialId, filename)),
  showNotification: (message: string) => dispatch(showNotification(message)),
  showErrorNotification: (
    message: string | React.ReactElement,
  ) => dispatch(showErrorNotification(message)),
  downloadInvoicesZip: (trial: Trial) => dispatch(downloadInvoicesZip(trial)),
  fetchTrialTags: () => dispatch(fetchTrialTags()),
  setPageSearchText: (query: string) => dispatch(setPageSearchText(pageIdentifier, query)),
  setYear: (year: string) => dispatch(setYear(pageIdentifier, year)),
  setType: (tags: number[]) => dispatch(setType(pageIdentifier, tags)),
  setSelectedTab: (selectedTab: 'institute' | 'individual') => dispatch(setSelectedTab(pageIdentifier, selectedTab)),
  setShowDeactivated: (show: boolean) => dispatch(setShowDeactivated(pageIdentifier, show)),
  setZerpaTableState:
    (zerpaState: ZerpaState) => dispatch(setZerpaTableState(pageIdentifier, zerpaState)),
  setLoading: (spinner: boolean) => dispatch(setLoading(spinner)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>

export default injectIntl(connector(TrialManagementTab));
